// Allow Hot Module Replacement (HMR) for JS files
if (import.meta.hot) {
  import.meta.hot.accept();
}

import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide.min.css';
import './card-carousel.scss';

/**
 * CardCarousel Component
 * Initializes Splide carousel with a minimal JS-only configuration
 */
class CardCarousel {
  constructor(element) {
    this.element = element;
    this.carousel = null;
    this.init();
  }

  init() {
    const splideElement = this.element.querySelector('.card-carousel__slides');

    if (!splideElement || this.element.dataset.initialized === 'true') {
      return;
    }

    const slideCount = splideElement.querySelectorAll('.splide__slide').length;
    const isSingleItem = slideCount === 1;

    if (slideCount === 0) {
      this.element.dataset.initialized = 'true';
      return;
    }

    let slidesPerPageDesktop;
    const desktopGap = '24px';
    let largeBreakpoint = null;
    switch (true) {
    case this.element.classList.contains('card-carousel--benefits'):
      slidesPerPageDesktop = 4.33;
      break;
    case this.element.classList.contains('card-carousel--horizontal'):
      slidesPerPageDesktop = 2.33;
      break;
    case this.element.classList.contains('card-carousel--vertical-wide'):
      slidesPerPageDesktop = 1.66;
      break;
    case this.element.classList.contains('card-carousel--faq-card'):
      slidesPerPageDesktop = 4.33;
      largeBreakpoint = {
        perPage: 6.33,
        gap: 'var(--gap-2xl, 40px)',
        padding: 0,
        drag: false,
      };
      break;
    default:
      slidesPerPageDesktop = 3.33;
    }

    if (slideCount < (largeBreakpoint ? largeBreakpoint.perPage : slidesPerPageDesktop)) {
      splideElement.classList.add('hide-slide-controls');
    }

    const options = {
      type: 'slide',
      perPage: 1,
      fixedWidth: isSingleItem ? '' : 'calc((100% + 12px) / 1.33 - 12px)',
      gap: '12px',
      padding: 0,
      mediaQuery: 'min',
      drag: true,
      keyboard: false,
      rewind: false,
      arrows: false,
      pagination: false,
      speed: 400,
      easing: 'ease-in-out',
      snap: false,
      trimSpace: false,
      breakpoints: {
        960: {
          perPage: slidesPerPageDesktop,
          fixedWidth: 0,
          gap: desktopGap,
          padding: 0,
          drag: false,
        },
        ...(largeBreakpoint ? { 1400: largeBreakpoint } : {}),
      },
    };

    try {
      this.carousel = new Splide(splideElement, options);
      this.carousel.on('mounted refresh updated', () => this.removeRedundantAria(splideElement));
      this.carousel.mount();
      this.element.dataset.initialized = 'true';
      this.setupArrowButtons();
    } catch (error) {
      delete this.element.dataset.initialized;
      console.error('CardCarousel: Failed to initialize Splide', error);
    }
  }

  removeRedundantAria(splideElement) {
    splideElement.removeAttribute('role');
    splideElement.removeAttribute('aria-roledescription');

    splideElement.querySelectorAll('.splide__slide').forEach((slide) => {
      slide.removeAttribute('role');
      slide.removeAttribute('aria-roledescription');
    });
  }

  setupArrowButtons() {
    const prevButton = this.element.querySelector('.card-carousel__arrow--prev');
    const nextButton = this.element.querySelector('.card-carousel__arrow--next');
    const list = this.element.querySelector('.splide__list');
    const track = this.element.querySelector('.splide__track');

    let currentOffset = 0;
    const isDesktop = () => window.matchMedia('(min-width: 960px)').matches;

    const getSlideStep = () => {
      const firstSlide = list?.querySelector('.splide__slide');
      if (!firstSlide || !list) return 0;
      const gap = parseFloat(getComputedStyle(list).gap) || 0;
      return firstSlide.offsetWidth + gap;
    };

    const getMaxOffset = () => {
      if (!list || !track) return 0;

      const trackMarginRight = parseFloat(getComputedStyle(track).marginRight) || 0;
      const rightOverflow = Math.max(0, -trackMarginRight);
      const visibleTrackWidth = Math.max(0, track.offsetWidth - rightOverflow);

      return Math.max(0, list.scrollWidth - visibleTrackWidth);
    };

    const syncOffsetFromList = () => {
      if (!list) return;
      const transform = getComputedStyle(list).transform;

      if (!transform || transform === 'none') {
        currentOffset = 0;
        return;
      }

      const values = transform.match(/-?\d+\.?\d*/g);
      if (!values) {
        currentOffset = 0;
        return;
      }

      const translateX = transform.startsWith('matrix3d')
        ? Number(values[12] ?? 0)
        : Number(values[4] ?? 0);

      currentOffset = Math.max(0, Math.min(Math.abs(translateX), getMaxOffset()));
    };

    const applyOffset = (offset) => {
      if (!list) return;
      currentOffset = Math.max(0, Math.min(offset, getMaxOffset()));
      list.style.transition = 'transform 400ms ease-in-out';
      list.style.transform = `translateX(-${currentOffset}px)`;
      updateArrowState();
    };

    const updateArrowState = () => {
      if (!this.carousel) {
        return;
      }

      if (!isDesktop()) {
        const controller = this.carousel.Components.Controller;
        const endIndex = controller.getEnd();
        prevButton?.toggleAttribute('disabled', this.carousel.index <= 0);
        nextButton?.toggleAttribute('disabled', this.carousel.index >= endIndex);
        return;
      }

      prevButton?.toggleAttribute('disabled', currentOffset <= 0);
      nextButton?.toggleAttribute('disabled', currentOffset >= getMaxOffset());
    };

    prevButton?.addEventListener('click', () => {
      if (!isDesktop()) {
        const currentIndex = this.carousel?.index;

        if (typeof currentIndex !== 'number' || currentIndex <= 0) {
          return;
        }

        this.carousel?.go(currentIndex - 1);
        return;
      }

      syncOffsetFromList();
      applyOffset(currentOffset - getSlideStep());
    });

    nextButton?.addEventListener('click', () => {
      if (!isDesktop()) {
        const currentIndex = this.carousel?.index;
        const endIndex = this.carousel?.Components.Controller.getEnd();

        if (typeof currentIndex !== 'number' || typeof endIndex !== 'number' || currentIndex >= endIndex) {
          return;
        }

        this.carousel?.go(currentIndex + 1);
        return;
      }

      syncOffsetFromList();
      applyOffset(currentOffset + getSlideStep());
    });

    this.carousel?.on('moved', updateArrowState);

    this.carousel?.on('resized', () => {
      if (!list) {
        return;
      }

      if (!isDesktop()) {
        list.style.transition = '';
        currentOffset = 0;
        updateArrowState();
        return;
      }

      syncOffsetFromList();
      applyOffset(currentOffset);
    });

    updateArrowState();
  }
}

/**
 * Auto-initialize all CardCarousel instances on page
 */
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('.card-carousel');
  carousels.forEach((carousel) => {
    new CardCarousel(carousel);
  });
});
